import React, {useEffect, useState} from "react";
import {getAlbumStats, getArtistStats, getTrackStats} from "./actions";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCircleInfo, faX} from "@fortawesome/free-solid-svg-icons";
import spotifyIcon from "./spotify-icon.png";
import {OPEN_SPOTIFY} from "../util/util";

const formatDate = (date: Date) => date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
const formatMonth = (date: Date) => date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

export enum EntityType {
    Track,
    Artist,
    Album,
}

export interface EntityModalProps {
    entityType: EntityType,
    id: string,
    name: string,
    image: string,
    closeCallback: () => void,
}

export const EntityModal = (props: EntityModalProps) => {

    const [visibleData, setVisibleData] = useState<React.ReactElement>();

    useEffect(() => {
        setVisibleData(getLoadingElement());
        switch (props.entityType) {
            case EntityType.Track:
                getTrackStats(props.id).then(data => handleTrackData(data));
                break;
            case EntityType.Artist:
                getArtistStats(props.id).then(data => handleArtistData(data));
                break;
            case EntityType.Album:
                getAlbumStats(props.id).then(data => handleAlbumData(data));
        }
    }, []);

    const handleTrackData = (data: any) => {
        if (!data) {
            setVisibleData(getErrorElement());
            return;
        }
        setVisibleData(<TrackEntityModal data={data} name={props.name} id={props.id} image={props.image} />);
    }

    const handleArtistData = (data: any) => {
        if (!data) {
            setVisibleData(getErrorElement());
            return;
        }
        setVisibleData(<ArtistEntityModal data={data} name={props.name} id={props.id} image={undefined} />);
    }

    const handleAlbumData = (data: any) => {
        if (!data) {
            setVisibleData(getErrorElement());
            return;
        }
        setVisibleData(<AlbumEntityModal data={data} name={props.name} id={props.id} image={props.image} />);
    }

    const getLoadingElement = () => {
        return (
            <div>Loading stats for {props.name}...</div>
        );
    }

    const getErrorElement = () => {
        return (
            <div>An error occurred while getting stats for {props.name}</div>
        );
    }

    return (
        <div className={'fixed top-0 left-0 w-full h-full bg-black/70 flex justify-center items-center'}>
            <div className={'custom-scrollbar flex flex-col overflow-y-auto bg-mm-dark-2 p-4 rounded-lg text-mm-text w-[30rem] portrait:w-[90%] max-h-[80vh] portrait:max-h-[85vh] relative'}>
                <div onClick={props.closeCallback} className={'absolute top-[0.6rem] right-[0.6rem] cursor-pointer rounded-full w-[1.6rem] h-[1.6rem] flex items-center justify-center text-[0.65rem] text-mm-text bg-transparent transition-colors hover:bg-mm-gray'}>
                    <FontAwesomeIcon icon={faX} />
                </div>
                <div>{visibleData}</div>
            </div>
        </div>
    );
}

interface SubEntityModalProps {
    data: any,
    id: string,
    name: string,
    image: string | undefined,
}

// Reusable sub-component styles
const statCard = 'flex flex-col bg-mm-dark rounded py-[0.6rem] px-[0.8rem]';
const statLabel = 'text-[0.7rem] uppercase tracking-[0.04rem] text-mm-gray-light';
const statValue = 'text-2xl font-bold text-mm-cyan';
const statValueCentered = 'text-2xl font-bold text-mm-cyan flex-1 flex items-center';
const statValueSm = 'text-base font-bold text-mm-cyan flex-1 flex items-center';
const statRank = 'text-[0.75rem] text-mm-green';
const listSection = 'flex flex-col gap-1 py-2 px-[0.8rem] bg-mm-dark rounded';
const listLabel = 'text-[0.7rem] uppercase tracking-[0.04rem] text-mm-gray-light';
const listOl = 'mt-1 pl-[1.3rem] text-[0.9rem] text-mm-text list-decimal marker:text-mm-cyan [&>li]:py-[0.15rem]';

const TrackEntityModal = (props: SubEntityModalProps) => {

    let data = props.data;
    console.log(data);
    console.log(props.image)

    return (
        <div className={'flex flex-col gap-[0.6rem]'}>
            <div className={'flex flex-row items-center gap-4 pb-4 border-b border-mm-gray'}>
                <img className={'w-24 h-24 portrait:w-[4.5rem] portrait:h-[4.5rem] rounded object-cover'} src={props.image} alt={'album cover'}/>
                <div className={'flex flex-col gap-[0.4rem]'}>
                    <div className={'text-[1.4rem] portrait:text-[1.15rem] font-bold text-white'}>{props.name}</div>
                    <a className={'flex items-center gap-[0.4rem] text-mm-green no-underline text-[0.85rem] hover:underline'} href={OPEN_SPOTIFY + '/track/' + props.id} target={'_blank'} rel={'noreferrer'}>
                        <img src={spotifyIcon} alt={'Spotify'} className={'w-[1.2rem] h-[1.2rem]'}/>Open in Spotify
                    </a>
                </div>
            </div>
            <div className={'grid grid-cols-2 gap-[0.6rem]'}>
                <div className={statCard}>
                    <span className={statLabel}>Total Listens</span>
                    <span className={statValue}>{data.listenCount.second}</span>
                    <span className={statRank}>#{data.listenCount.first}</span>
                </div>
                <div className={statCard}>
                    <span className={statLabel}>Total Minutes</span>
                    <span className={statValue}>{Math.round(data.listenTime.second / 60)}</span>
                    <span className={statRank}>#{data.listenTime.first}</span>
                </div>
                <div className={statCard}>
                    <span className={statLabel}>Release Year</span>
                    <span className={statValueCentered}>{data.releaseYear}</span>
                </div>
                <div className={statCard}>
                    <span className={statLabel}>First Listen</span>
                    <span className={statValueSm}>{formatDate(new Date(data.firstListenAt))}</span>
                </div>
            </div>
            <div className={listSection}>
                <div className={listLabel}>Leaderboard</div>
                <ol className={listOl}>
                    {data.leaderboard.slice(0, 3).map((item: string, i: number) => <li key={i}>{item}</li>)}
                </ol>
            </div>
            <div className={'flex flex-row portrait:flex-col gap-[0.6rem]'}>
                <div className={listSection + ' flex-1'}>
                    <div className={listLabel}>Top Days</div>
                    <ol className={listOl}>
                        {data.topDays.slice(0, 3).map((item: any, i: number) => <li key={i}>{formatDate(new Date(item.first))}</li>)}
                    </ol>
                </div>
                <div className={listSection + ' flex-1'}>
                    <div className={listLabel}>Top Months</div>
                    <ol className={listOl}>
                        {data.topMonths.slice(0, 3).map((item: any, i: number) => <li key={i}>{formatMonth(new Date(item.first))}</li>)}
                    </ol>
                </div>
            </div>
        </div>
    );
}

const ArtistEntityModal = (props: SubEntityModalProps) => {

    let data = props.data;
    console.log(data);

    return (
        <div className={'flex flex-col gap-[0.6rem]'}>
            <div className={'flex flex-row items-center gap-4 pb-4 border-b border-mm-gray'}>
                <div className={'flex flex-col gap-[0.4rem]'}>
                    <div className={'text-[1.4rem] portrait:text-[1.15rem] font-bold text-white'}>{props.name}</div>
                    <a className={'flex items-center gap-[0.4rem] text-mm-green no-underline text-[0.85rem] hover:underline'} href={OPEN_SPOTIFY + '/artist/' + props.id} target={'_blank'} rel={'noreferrer'}>
                        <img src={spotifyIcon} alt={'Spotify'} className={'w-[1.2rem] h-[1.2rem]'}/>Open in Spotify
                    </a>
                </div>
            </div>
            <div className={'grid grid-cols-2 gap-[0.6rem]'}>
                <div className={statCard}>
                    <span className={statLabel}>Total Listens</span>
                    <span className={statValue}>{data.listenCount.second}</span>
                    <span className={statRank}>#{data.listenCount.first}</span>
                </div>
                <div className={statCard}>
                    <span className={statLabel}>Total Minutes</span>
                    <span className={statValue}>{Math.round(data.listenTime.second / 60)}</span>
                    <span className={statRank}>#{data.listenTime.first}</span>
                </div>
                <div className={statCard}>
                    <span className={statLabel}>Diversity Score <span className={'group relative inline-flex items-center'}><FontAwesomeIcon icon={faCircleInfo} className={'text-mm-gray cursor-help text-[0.6rem]'} /><span className={'invisible opacity-0 absolute bottom-[130%] left-1/2 -translate-x-1/2 bg-mm-dark-3 text-mm-text text-[0.75rem] normal-case tracking-normal py-[0.4rem] px-[0.6rem] rounded w-48 text-center transition-opacity pointer-events-none group-hover:visible group-hover:opacity-100'}>% of listens beyond your top track by this artist</span></span></span>
                    <span className={statValueCentered}>{data.diversity}</span>
                </div>
                <div className={statCard}>
                    <span className={statLabel}>Unique Tracks</span>
                    <span className={statValueCentered}>{data.uniqueTracks}</span>
                </div>
                <div className={statCard}>
                    <span className={statLabel}>Unique Albums</span>
                    <span className={statValueCentered}>{data.uniqueAlbums}</span>
                </div>
                <div className={statCard}>
                    <span className={statLabel}>First Listen</span>
                    <span className={statValueSm}>{formatDate(new Date(data.firstTrack.timestamp))}</span>
                    <span className={statRank}>{data.firstTrack.track}</span>
                </div>
            </div>
            <div className={listSection}>
                <div className={listLabel}>Leaderboard</div>
                <ol className={listOl}>
                    {data.leaderboard.slice(0, 3).map((item: string, i: number) => <li key={i}>{item}</li>)}
                </ol>
            </div>
            <div className={listSection}>
                <div className={listLabel}>Top Tracks</div>
                <ol className={listOl}>
                    {data.topTracks.slice(0, 3).map((item: any, i: number) => <li key={i}>{item?.track}</li>)}
                </ol>
            </div>
            <div className={listSection}>
                <div className={listLabel}>Top Albums</div>
                <ol className={listOl}>
                    {data.topAlbums.slice(0, 3).map((item: any, i: number) => <li key={i}>{item?.album}</li>)}
                </ol>
            </div>
            <div className={'flex flex-row portrait:flex-col gap-[0.6rem]'}>
                <div className={listSection + ' flex-1'}>
                    <div className={listLabel}>Top Days</div>
                    <ol className={listOl}>
                        {data.topDays.slice(0, 3).map((item: any, i: number) => <li key={i}>{formatDate(new Date(item.first))}</li>)}
                    </ol>
                </div>
                <div className={listSection + ' flex-1'}>
                    <div className={listLabel}>Top Months</div>
                    <ol className={listOl}>
                        {data.topMonths.slice(0, 3).map((item: any, i: number) => <li key={i}>{formatMonth(new Date(item.first))}</li>)}
                    </ol>
                </div>
            </div>
        </div>
    );
}

const AlbumEntityModal = (props: SubEntityModalProps) => {

    let data = props.data;
    console.log(data);

    return (
        <div className={'flex flex-col gap-[0.6rem]'}>
            <div className={'flex flex-row items-center gap-4 pb-4 border-b border-mm-gray'}>
                <img className={'w-24 h-24 portrait:w-[4.5rem] portrait:h-[4.5rem] rounded object-cover'} src={props.image} alt={'album cover'}/>
                <div className={'flex flex-col gap-[0.4rem]'}>
                    <div className={'text-[1.4rem] portrait:text-[1.15rem] font-bold text-white'}>{props.name}</div>
                    <a className={'flex items-center gap-[0.4rem] text-mm-green no-underline text-[0.85rem] hover:underline'} href={OPEN_SPOTIFY + '/album/' + props.id} target={'_blank'} rel={'noreferrer'}>
                        <img src={spotifyIcon} alt={'Spotify'} className={'w-[1.2rem] h-[1.2rem]'}/>Open in Spotify
                    </a>
                </div>
            </div>
            <div className={'grid grid-cols-2 gap-[0.6rem]'}>
                <div className={statCard}>
                    <span className={statLabel}>Total Listens</span>
                    <span className={statValue}>{data.listenCount.second}</span>
                    <span className={statRank}>#{data.listenCount.first}</span>
                </div>
                <div className={statCard}>
                    <span className={statLabel}>Total Minutes</span>
                    <span className={statValue}>{Math.round(data.listenTime.second / 60)}</span>
                    <span className={statRank}>#{data.listenTime.first}</span>
                </div>
                <div className={statCard}>
                    <span className={statLabel}>Release Year</span>
                    <span className={statValueCentered}>{data.releaseYear}</span>
                </div>
                <div className={statCard}>
                    <span className={statLabel}>First Listen</span>
                    <span className={statValueSm}>{formatDate(new Date(data.firstTrack.timestamp))}</span>
                    <span className={statRank}>{data.firstTrack.track}</span>
                </div>
            </div>
            <div className={listSection}>
                <div className={listLabel}>Leaderboard</div>
                <ol className={listOl}>
                    {data.leaderboard.slice(0, 3).map((item: string, i: number) => <li key={i}>{item}</li>)}
                </ol>
            </div>
            <div className={listSection}>
                <div className={listLabel}>Top Tracks</div>
                <ol className={listOl}>
                    {data.topTracks.slice(0, 3).map((item: any, i: number) => <li key={i}>{item?.track}</li>)}
                </ol>
            </div>
            <div className={'flex flex-row portrait:flex-col gap-[0.6rem]'}>
                <div className={listSection + ' flex-1'}>
                    <div className={listLabel}>Top Days</div>
                    <ol className={listOl}>
                        {data.topDays.slice(0, 3).map((item: any, i: number) => <li key={i}>{formatDate(new Date(item.first))}</li>)}
                    </ol>
                </div>
                <div className={listSection + ' flex-1'}>
                    <div className={listLabel}>Top Months</div>
                    <ol className={listOl}>
                        {data.topMonths.slice(0, 3).map((item: any, i: number) => <li key={i}>{formatMonth(new Date(item.first))}</li>)}
                    </ol>
                </div>
            </div>
        </div>
    );
}
